import { useT } from '@/i18n';

const RADIUS = 7;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * The chapter dwell gate, shown as elapsed progress instead of an indeterminate spinner.
 *
 * The dwell clock already ticks once a second in the training route, so the remaining time
 * is known — withholding it only made a bounded wait feel unbounded. This is presentation
 * ONLY: the gate itself is `isChapterComplete`, and the evidence is the server's own
 * re-derivation from its event timestamps (`bozp-session-gate.ts`). Nothing here unlocks
 * anything.
 *
 * The ring carries no information the label lacks, so it is `aria-hidden`; the label is a
 * plain node rather than a live region, because announcing a new value every second would
 * bury a screen-reader user in noise for a bounded, self-resolving wait.
 */
export function DwellCountdown({
  remainingSeconds,
  totalSeconds,
}: {
  remainingSeconds: number;
  totalSeconds: number;
}) {
  const t = useT();
  const elapsed = Math.max(0, totalSeconds - remainingSeconds);
  const progress = totalSeconds > 0 ? Math.min(1, elapsed / totalSeconds) : 1;

  return (
    <p
      data-testid="dwell-countdown"
      data-remaining={remainingSeconds}
      className="mb-4 flex items-center gap-2.5 text-sm text-ink-faint"
    >
      <svg aria-hidden viewBox="0 0 18 18" className="h-3.5 w-3.5 shrink-0 -rotate-90">
        <circle
          cx="9"
          cy="9"
          r={RADIUS}
          fill="none"
          strokeWidth="2"
          className="stroke-line-strong"
        />
        <circle
          cx="9"
          cy="9"
          r={RADIUS}
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={CIRCUMFERENCE * (1 - progress)}
          className="stroke-fixit-600 transition-[stroke-dashoffset] duration-1000 ease-linear motion-reduce:transition-none"
        />
      </svg>
      {t('{seconds} s left', { seconds: remainingSeconds })}
    </p>
  );
}
